import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { getProject } from "@/lib/projects";
import { Reveal } from "@/components/site/Reveal";
import { CTAButtons } from "@/components/site/CTAButtons";
import { LazyMap } from "@/components/site/LazyMap";
import { applySeo } from "@/lib/seo";
import { SITE_URL } from "@/data/site";

const Eyebrow = ({ children }) => (
    <span className="eyebrow">{children}</span>
);

const NotFound = () => (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif text-4xl text-charcoal">Residence not found</h1>
        <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange"
        >
            <ArrowLeft size={15} /> Back to home
        </Link>
    </div>
);

export default function ProjectDetail() {
    const { id } = useParams();
    const p = getProject(id || "dlf-westpark");

    useEffect(() => {
        if (!p) return;
        const url = `${SITE_URL}/projects/${p.id}`;
        applySeo({
            title: `${p.name} by ${p.developer}, ${p.location} | Swastik Realty`,
            description: `${p.name} by ${p.developer} in ${p.location}. ${p.configurations} · ${p.priceRange || p.startingPrice} · Possession ${p.possession}. Get the brochure, floor plans & price sheet from Swastik Realty.`,
            canonical: url,
            image: p.heroImage,
            jsonLd: [
                {
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: p.name,
                    image: p.heroImage,
                    description: `${p.name} by ${p.developer} — ${p.configurations} luxury residences in ${p.location}.`,
                    brand: { "@type": "Organization", name: p.developer },
                    offers: {
                        "@type": "Offer",
                        priceCurrency: "INR",
                        availability: "https://schema.org/InStock",
                        url,
                    },
                },
                {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                        { "@type": "ListItem", position: 2, name: "Signature Collection", item: `${SITE_URL}/#signature` },
                        { "@type": "ListItem", position: 3, name: p.name, item: url },
                    ],
                },
                {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: p.faq.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
            ],
        });
    }, [p]);

    if (!p) return <NotFound />;

    return (
        <main data-testid="project-detail" className="bg-ivory pt-[76px]">
            {/* PROJECT HERO — clean image first, details separately */}
            <section data-testid="project-hero" className="bg-ivory px-4 pb-10 pt-8 sm:px-6 lg:px-10 lg:pb-16 lg:pt-12">
                <div className="mx-auto max-w-[1400px]">
                    <Link
                        to="/"
                        data-testid="project-back"
                        className="mb-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-slatey transition-colors hover:text-orange"
                    >
                        <ArrowLeft size={14} /> Signature Collection
                    </Link>

                    <div className="grid overflow-hidden border border-line bg-white lg:grid-cols-[1.15fr_0.85fr]">
                        <div className="relative min-h-[430px] bg-stone sm:min-h-[560px] lg:min-h-[680px]">
                            <motion.img
                                src={p.heroImage}
                                alt={`${p.name} — ${p.developer}`}
                                initial={{ scale: 1.03, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                className="h-full w-full object-cover object-center"
                            />
                        </div>

                        <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
                            <span className="eyebrow">{p.developer}</span>
                            <h1 className="mt-4 font-serif text-4xl leading-[1.02] text-charcoal sm:text-5xl lg:text-6xl">
                                {p.name}
                            </h1>
                            <div className="mt-5 flex items-start gap-2 text-sm leading-relaxed text-slatey">
                                <MapPin size={16} className="mt-0.5 shrink-0 text-orange" />
                                <span>{p.location}</span>
                            </div>

                            <p className="mt-7 text-base leading-relaxed text-slatey">
                                {p.tagline}
                            </p>
                            {p.audienceLine && (
                                <p className="mt-3 text-sm leading-relaxed text-charcoal/75">
                                    {p.audienceLine}
                                </p>
                            )}

                            <div className="mt-8 grid grid-cols-2 border-y border-line">
                                <div className="border-r border-line py-5 pr-4">
                                    <span className="block text-[10px] uppercase tracking-[0.15em] text-slatey">Configuration</span>
                                    <span className="mt-2 block font-serif text-xl text-charcoal">{p.configurations}</span>
                                </div>
                                <div className="py-5 pl-5">
                                    <span className="block text-[10px] uppercase tracking-[0.15em] text-slatey">Starting from</span>
                                    <span className="mt-2 block font-serif text-xl text-orange">{p.startingPrice}</span>
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slatey">
                                {p.highlights.slice(0, 3).map((item, i) => (
                                    <span key={i}>{item}</span>
                                ))}
                                <span>RERA: {p.maharera}</span>
                            </div>

                            <CTAButtons
                                project={p}
                                keys={["whatsapp", "sitevisit", "brochure"]}
                                className="mt-9"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* OVERVIEW + SNAPSHOT */}
            <section className="px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-12 lg:gap-16">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>Overview</Eyebrow>
                            <h2 className="mt-5 max-w-xl font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                                {p.tagline}
                            </h2>
                            <div className="mt-7 space-y-5">
                                {p.overview.map((para, i) => (
                                    <p key={i} className="text-base leading-relaxed text-slatey">
                                        {para}
                                    </p>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            <div className="border border-line bg-sand p-8">
                                <Eyebrow>Snapshot</Eyebrow>
                                <dl className="mt-6 divide-y divide-line">
                                    {Object.entries(p.snapshot).map(([k, v]) => (
                                        <div key={k} className="flex items-start justify-between gap-6 py-3.5">
                                            <dt className="text-xs uppercase tracking-[0.12em] text-slatey">{k}</dt>
                                            <dd className="text-right text-sm font-medium text-charcoal">{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PRICING / CONFIGURATIONS / AVAILABLE PLANS */}
            <section className="bg-stone px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <Eyebrow>Pricing & Available Plans</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                            Configurations & guide pricing.
                        </h2>
                    </Reveal>

                    <div className="mt-10 overflow-hidden border border-line bg-white">
                        <div className="hidden grid-cols-12 gap-4 border-b border-line bg-sand px-7 py-4 text-[10px] uppercase tracking-[0.15em] text-slatey md:grid">
                            <div className="col-span-3">Configuration</div>
                            <div className="col-span-3">Carpet Area</div>
                            <div className="col-span-3">Starting Price</div>
                            <div className="col-span-3 text-right">Plan</div>
                        </div>
                        {p.plans.map((pl, i) => (
                            <Reveal key={i}>
                                <div className="grid grid-cols-1 gap-3 border-b border-line px-7 py-6 last:border-b-0 md:grid-cols-12 md:items-center md:gap-4">
                                    <div className="md:col-span-3">
                                        <p className="font-serif text-xl text-charcoal">{pl.config}</p>
                                    </div>
                                    <div className="md:col-span-3 text-sm text-slatey">{pl.carpet}</div>
                                    <div className="md:col-span-3 font-serif text-lg text-orange">{pl.price}</div>
                                    <div className="md:col-span-3 md:text-right">
                                        <CTAButtons project={p} keys={["floorplan"]} className="md:justify-end" />
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <CTAButtons project={p} keys={["pricesheet", "sitevisit"]} className="mt-8" />
                </div>
            </section>

            {/* FLOOR PLANS */}
            <section className="px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <Eyebrow>Floor Plans</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                            Considered layouts, honestly presented.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {p.plans.map((pl, i) => (
                            <Reveal key={i} delay={(i % 3) * 0.08}>
                                <div className="border border-line bg-white">
                                    <div className="border-b border-line bg-sand">
                                        <img src={pl.floorPlan} alt={`${pl.config} floor plan`} loading="lazy" className="h-56 w-full object-contain p-4" />
                                    </div>
                                    <div className="flex items-center justify-between p-6">
                                        <div>
                                            <p className="font-serif text-lg text-charcoal">{pl.config}</p>
                                            <p className="text-xs text-slatey">{pl.carpet}</p>
                                        </div>
                                        <CTAButtons project={p} keys={["floorplan"]} />
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* GALLERY */}
            <section className="bg-charcoal px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <span className="eyebrow">Gallery</span>
                        <h2 className="mt-5 font-serif text-3xl leading-tight text-white lg:text-4xl">
                            A closer look.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
                        {p.gallery.map((src, i) => (
                            <Reveal key={i} delay={(i % 3) * 0.06}>
                                <div className={`group overflow-hidden ${i % 5 === 0 ? "sm:row-span-2" : ""}`}>
                                    <img
                                        src={src}
                                        alt={`${p.name} gallery ${i + 1}`}
                                        loading="lazy"
                                        className="h-56 w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-110 lg:h-72"
                                    />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* AMENITIES */}
            <section className="px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <Eyebrow>Amenities</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                            Every day, elevated.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-px overflow-hidden border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
                        {p.amenities.map((a, i) => (
                            <Reveal key={a} delay={(i % 3) * 0.05}>
                                <div className="flex items-center gap-4 bg-ivory p-6">
                                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-champagne/60 text-orange">
                                        <Check size={16} />
                                    </span>
                                    <span className="text-sm text-charcoal">{a}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* LOCATION + MAP */}
            <section className="bg-sand px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-2 lg:gap-16">
                    <div>
                        <Reveal>
                            <Eyebrow>Location Advantages</Eyebrow>
                            <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                                Connected to everything that matters.
                            </h2>
                            <ul className="mt-8 divide-y divide-line border-y border-line">
                                {p.locationAdvantages.map((l) => (
                                    <li key={l.label} className="flex items-center justify-between py-4">
                                        <span className="flex items-center gap-3 text-sm text-charcoal">
                                            <MapPin size={15} className="text-orange" />
                                            {l.label}
                                        </span>
                                        <span className="text-sm font-medium text-slatey">{l.time}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="h-full min-h-[340px] overflow-hidden border border-line">
                            <LazyMap query={p.mapQuery} title={`Map of ${p.name}`} />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* INVESTMENT HIGHLIGHTS */}
            <section className="px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <Eyebrow>Investment Highlights</Eyebrow>
                        <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                            Why this address makes sense.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {p.highlights.map((h, i) => (
                            <Reveal key={i} delay={(i % 2) * 0.08}>
                                <div className="flex gap-5 border border-line bg-white p-7">
                                    <span className="font-serif text-3xl text-champagne">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <p className="text-base leading-relaxed text-slatey">{h}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT DEVELOPER */}
            <section className="bg-stone px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto grid max-w-[1400px] items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <Reveal>
                        <div className="relative aspect-[4/3] overflow-hidden border border-line">
                            <img src={p.aboutDeveloper.image} alt={p.aboutDeveloper.name} loading="lazy" className="h-full w-full object-cover" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div>
                            <Eyebrow>About the Developer</Eyebrow>
                            <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                                {p.aboutDeveloper.name}
                            </h2>
                            <p className="mt-6 max-w-md text-base leading-relaxed text-slatey">
                                {p.aboutDeveloper.blurb}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MAHARERA */}
            <section className="px-6 py-16 lg:px-10">
                <div className="mx-auto max-w-[1400px]">
                    <Reveal>
                        <div className="flex flex-col items-start gap-5 border border-line bg-sand p-8 sm:flex-row sm:items-center sm:justify-between lg:p-10">
                            <div className="flex items-start gap-4">
                                <ShieldCheck size={26} className="mt-1 flex-none text-orange" />
                                <div>
                                    <p className="text-[10px] uppercase tracking-luxe text-slatey">MahaRERA Details</p>
                                    <p className="mt-2 font-mono text-sm tracking-wider text-charcoal">{p.maharera}</p>
                                    <p className="mt-2 max-w-xl text-xs leading-relaxed text-slatey">
                                        Swastik Realty is an authorised channel partner. This project is
                                        registered with MahaRERA; the registration number will be displayed
                                        here once updated.
                                    </p>
                                </div>
                            </div>
                            <CTAButtons project={p} keys={["pricesheet"]} />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* FAQ */}
            <section className="px-6 py-20 lg:px-10 lg:py-28">
                <div className="mx-auto max-w-[900px]">
                    <Reveal>
                        <div className="mb-10 text-center">
                            <Eyebrow>Frequently Asked</Eyebrow>
                            <h2 className="mt-5 font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                                Good to know.
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <Accordion type="single" collapsible className="w-full">
                            {p.faq.map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`} className="border-line">
                                    <AccordionTrigger className="text-left font-serif text-lg text-charcoal hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-base leading-relaxed text-slatey">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative overflow-hidden bg-charcoal px-6 py-24 lg:px-10 lg:py-32">
                <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] translate-x-1/3 -translate-y-1/3 rounded-full bg-orange/20 blur-[130px]" />
                <div className="relative mx-auto max-w-[900px] text-center">
                    <Reveal>
                        <span className="eyebrow">Enquire</span>
                        <h2 className="mx-auto mt-6 max-w-2xl font-serif text-4xl leading-[1.05] text-white lg:text-5xl">
                            Interested in <span className="italic text-champagne">{p.name}</span>?
                        </h2>
                        <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-white/70">
                            Share your details and our advisors will send the brochure, floor plans and
                            latest price sheet — and arrange a private site visit.
                        </p>
                        <CTAButtons
                            project={p}
                            keys={["whatsapp", "sitevisit", "pricesheet", "consultation"]}
                            onImage
                            className="mt-10 justify-center"
                        />
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
